import React, { useMemo } from 'react';
import { Divider, Tag, Typography, Button, Alert } from 'antd';
import { Price } from '@partsbadger/library';

const { Text } = Typography;

type Props = {
    quote: any;
    hidePrices?: boolean;
    mode: 'rapid_quote' | 'quote_detail';
    onEdit?: () => void;
    onRequote?: () => void;
    isReorder?: boolean;
    salesOrderRequirements?: string[];
};

export function AdditionalRequirementsSummaryCard({
    quote,
    hidePrices = false,
    mode,
    onEdit,
    onRequote,
    isReorder = false,
    salesOrderRequirements = [],
}: Props) {
    const canEdit = mode === 'rapid_quote';

    const customReqs = useMemo(() => (quote?.custom_requirements ?? []) as any[], [quote]);
    const appCostItems = useMemo(() => (quote?.application_cost_items ?? []) as any[], [quote]);
    const requirementLabels = useMemo(() => {
        const labels = [...customReqs.map((i: any) => i?.label), ...salesOrderRequirements]
            .map(label => String(label ?? '').trim())
            .filter(Boolean);

        return Array.from(new Set(labels));
    }, [customReqs, salesOrderRequirements]);

    const applicationCost = useMemo(() => {
        const v = quote?.application_cost;
        const n = typeof v === 'number' ? v : v ? Number(v) : 0;
        return Number.isFinite(n) ? n : 0;
    }, [quote]);

    const showFallbackAppCostRow = appCostItems.length === 0 && applicationCost > 0;

    const showRequoteAction = !isReorder && !canEdit;

    return (
        <div>
            <div className="flex justify-between items-center">
                <Text strong>Additional Requirements</Text>

                {showRequoteAction ? (
                    <Button size="small" type="link" onClick={onRequote} disabled={!onRequote}>
                        Request a requote
                    </Button>
                ) : null}
            </div>

            {!canEdit && !isReorder && (
                <Alert
                    className="mt-2"
                    type="info"
                    showIcon
                    message="Additional requirements can’t be edited at checkout."
                    description={<span>If you need to change them, please request a requote.</span>}
                />
            )}

            <Divider className="my-3" />

            <div>
                <Text strong>Quote includes</Text>
                <div className="flex flex-wrap gap-2 mt-2">
                    {requirementLabels.length ? (
                        requirementLabels.map(label => <Tag key={label}>{label}</Tag>)
                    ) : (
                        <Text type="secondary">None</Text>
                    )}
                </div>
            </div>

            <Divider className="my-3" />

            <div>
                <Text strong>Additional and Application Costs</Text>

                <div className="mt-2">
                    {appCostItems.length ? (
                        appCostItems.map((i: any, idx: number) => (
                            <div
                                key={i.id ?? `${i.additional_requirement ?? 'app-cost'}-${idx}`}
                                className="flex flex-row justify-between mb-1"
                            >
                                <div>{i.additional_requirement ?? 'Application Cost'}</div>
                                <div>{hidePrices ? 'Pending' : <Price value={Number(i.price ?? 0)} />}</div>
                            </div>
                        ))
                    ) : showFallbackAppCostRow ? (
                        <div className="flex flex-row justify-between mb-1">
                            <div>Application Cost</div>
                            <div>{hidePrices ? 'Pending' : <Price value={applicationCost} />}</div>
                        </div>
                    ) : (
                        <Text type="secondary">None</Text>
                    )}
                </div>
            </div>
        </div>
    );
}
